// Account settings: profile photo, supervision, sharing pause, approximate sharing, signed-in devices, Home Assistant API
// tokens, passkeys, emergency contacts, data export and account deletion. Actions the caller's supervision
// locks become requests to their guardians.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import {
  DEFAULT_TOKEN_NAME,
  deletionConfirmed,
  exportFileName,
  lastActiveText,
  lastUsedText,
  platformLabel,
  sortDevices,
  sortTokens,
  TOKEN_NAME_MAX,
  tokenCircleName,
  tokenScopeText,
  validTokenName,
} from "../account/account";
import { useAuth, useMe } from "../auth/AuthContext";
import { useBlocks, useSetBlocked } from "../chat/useChatAccount";
import { tokenCircles } from "../circles/circles";
import { BubbleForm } from "../components/BubbleForm";
import { AccountSupervisionCard, askError, AskControl, RequestState } from "../components/MySupervision";
import { ProfilePhotoCard } from "../components/ProfilePhotoCard";
import {
  CONTACT_NAME_MAX,
  contactErrorText,
  contactStatus,
  contactStatusText,
  MAX_CONTACTS,
  validContact,
} from "../contacts/contacts";
import { isPasskeyCancelled, passkeysSupported, registerPasskey } from "../auth/passkeys";
import { activeBubbles, bubbleScopeText, radiusText, untilText } from "../sharing/bubbles";
import { useSharingBubbles } from "../sharing/useSharingBubbles";
import { storeMovingTrailsShown, useMovingTrailsShown } from "../live/movingTrails";
import { storeTravelTogether, useTravelTogether } from "../live/travelTogether";
import { resolveUnits, storeUnitsChoice, UNITS_CHOICES, type UnitsChoice } from "../units";
import {
  actionMode,
  bubbleAction,
  bubbleParams,
  myRequestText,
  noticeText,
  recentRequests,
  SUPERVISION_POLL_MS,
  supervisionPoll,
} from "../supervision/member";
import { MY_REQUESTS_KEY, useAsk, useMyRequests } from "../supervision/useMyRequests";
import { useNow } from "../useNow";
import { useUnits, useUnitsChoice } from "../useUnits";

type User = Schemas["User"];
type Device = Schemas["Device"];
type ApiToken = Schemas["ApiToken"];
type ApiTokenCreated = Schemas["ApiTokenCreated"];
type EmergencyContact = Schemas["EmergencyContact"];

/** Refreshes "Active 5 min ago" labels this often. */
const TICK_MS = 60_000;
const COPIED_MS = 2_000;
const DEVICES_KEY = ["me", "devices"] as const;
const TOKENS_KEY = ["me", "api-tokens"] as const;
const CONTACTS_KEY = ["me", "emergency-contacts"] as const;

/** The caller's circles: their supervisions decide which account-wide actions need a guardian. */
function useCircles() {
  return useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
    refetchInterval: (query) => supervisionPoll(query.state.data),
  });
}

/** DELETE where "already gone" is what we wanted. */
async function deleteIdempotent(result: Promise<{ error?: unknown; response: Response }>): Promise<void> {
  const settled = await result;
  if (!settled.response.ok && settled.response.status !== 404) unwrap({ ...settled, data: undefined });
}

export function AccountPage() {
  const me = useMe();
  const nowMs = useNow(TICK_MS);
  return (
    <div className="stack account-page">
      <h1>{me.data ? me.data.name : "Account"}</h1>
      {me.error && <p className="error">{errorMessage(me.error)}</p>}
      {me.data && <p className="muted">{me.data.email}</p>}
      {me.data && <ProfilePhotoCard user={me.data} />}
      {me.data && <AccountSupervisionCard selfId={me.data.id} selfName={me.data.name} />}
      {me.data && <SharingCard user={me.data} />}
      {me.data && <ApproximateSharingCard user={me.data} nowMs={nowMs} />}
      {me.data && <ChatCard user={me.data} />}
      <UnitsCard />
      <MapCard />
      <DevicesCard nowMs={nowMs} />
      <ApiTokensCard nowMs={nowMs} />
      {passkeysSupported() && <AddPasskeyCard />}
      <EmergencyContactsCard nowMs={nowMs} />
      {me.data && <YourDataCard user={me.data} />}
    </div>
  );
}

function SharingCard({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const circles = useCircles().data ?? [];
  const mode = actionMode("pause_global", circles);
  const nowMs = useNow(TICK_MS);
  const requests = useMyRequests(circles.some((c) => c.supervision));
  // Once approved, sharing is paused and the Ask button gives way to Resume: the outcome still shows here.
  const decided = recentRequests(requests.data ?? [], ["pause_global"], nowMs).find((r) => r.status !== "pending");
  const update = useMutation({
    mutationFn: async (sharing: User["sharing"]) => unwrap(await api.PATCH("/me", { body: { sharing } })),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["me"], updated);
      // Member lists report effective sharing, which includes this switch.
      await queryClient.invalidateQueries({ queryKey: ["circle"] });
    },
    // A supervision started or tightened since the page loaded: show the Ask button.
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "supervised")
        await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
  const paused = user.sharing === "paused";

  function toggle() {
    if (!paused) {
      const question =
        "Pause location sharing? Nobody in any of your circles will see your location until you resume, " +
        "and locations recorded while paused are discarded." +
        (noticeText(mode) ? ` ${noticeText(mode)}` : "");
      if (!window.confirm(question)) return;
    }
    update.mutate(paused ? "active" : "paused");
  }

  return (
    <section className="card stack">
      <h2>Location sharing</h2>
      <p className={paused ? "error" : "muted"} role="status">
        {paused
          ? "Sharing is paused everywhere. Your circles can't see your location."
          : "Your circles can see your location. To pause one circle only, use its page."}
      </p>
      {update.error && (
        <p className="error" role="alert">
          {update.error instanceof ApiError && update.error.code === "supervised"
            ? "Pausing everywhere needs a guardian's approval now. Ask them below."
            : errorMessage(update.error)}
        </p>
      )}
      {!paused && mode === "ask" ? (
        <AskControl
          action="pause_global"
          label="Ask to pause everywhere"
          intro={
            <p className="muted">
              Your supervision asks a guardian before you pause sharing everywhere. It pauses once a guardian approves
              in each circle that asks for it. You can still pause a circle that doesn't supervise you on its own page.
            </p>
          }
        />
      ) : (
        <>
          {decided && <RequestState request={decided} label="Pause sharing everywhere" />}
          <div className="button-row">
            <button
              type="button"
              className={paused ? undefined : "secondary"}
              disabled={update.isPending}
              onClick={toggle}
            >
              {paused ? "Resume sharing" : "Pause sharing everywhere"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function ApproximateSharingCard({ user, nowMs }: { user: User; nowMs: number }) {
  const units = useUnits();
  const circles = useCircles();
  const circleList = circles.data ?? [];
  // A guardian may start or end an area for one of the caller's circles.
  const { bubbles, loading, listError, start, end, busy, error } = useSharingBubbles(
    circleList.some((c) => c.supervision) ? SUPERVISION_POLL_MS : false,
  );
  const active = activeBubbles(bubbles, nowMs);
  const ask = useAsk();
  const requests = useMyRequests(circleList.some((c) => c.supervision));
  const asks = (circleId: string | null) => actionMode(bubbleAction(circleId), circleList, circleId) === "ask";
  const shownRequests = recentRequests(requests.data ?? [], ["bubble_circle", "bubble_everywhere"], nowMs);
  const circleName = (id: string) => circleList.find((c) => c.id === id)?.name;

  return (
    <section className="card stack">
      <h2>Approximate sharing</h2>
      <p className="muted">
        Share an area instead of your location. Your circles see a circle on the map somewhere around you, not where you
        are; your history, places and drives are left out while it's on.
        {user.sharing === "paused" && " Sharing is paused everywhere, so nobody sees even the area until you resume."}
      </p>
      {(listError ?? error) && (
        <p className="error" role="alert">
          {listError ?? error}
        </p>
      )}
      {ask.error && (
        <p className="error" role="alert">
          {askError(ask.error)}
        </p>
      )}
      {shownRequests.map((request) => (
        <RequestState key={request.id} request={request} label={myRequestText(request, circleName)} />
      ))}
      {active.length > 0 && (
        <ul className="settings-list">
          {active.map((bubble) => {
            const until = untilText(bubble.until, nowMs);
            return (
              <li key={bubble.id} className="settings-item">
                <span className="settings-item-text">
                  <span className="settings-item-name">{circles.data ? bubbleScopeText(bubble, circleList) : "…"}</span>
                  <span className="muted settings-item-meta">
                    Within {radiusText(bubble.radius_m, units)} · {until ?? "until you turn it off"}
                  </span>
                </span>
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={() => end.mutate(bubble)}
                  aria-label={`Turn off approximate sharing for ${bubbleScopeText(bubble, circleList)}`}
                >
                  Turn off
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!loading && circles.data && circleList.length > 0 && (
        <BubbleForm
          circles={circleList}
          busy={start.isPending || ask.isPending}
          askFor={asks}
          onStart={(choice) =>
            asks(choice.circleId)
              ? ask.mutate({
                  action: bubbleAction(choice.circleId),
                  params: bubbleParams(choice.circleId, choice.radiusM, choice.hours),
                })
              : start.mutate(choice)
          }
        />
      )}
      {circles.data && circleList.length === 0 && <p className="muted">Join a circle to share with it.</p>}
    </section>
  );
}

const UNITS_LABELS: Record<UnitsChoice, string> = {
  auto: "Automatic",
  metric: "Kilometres (km, km/h)",
  imperial: "Miles (mi, mph)",
};

/** Read receipts & typing (both ways, so off for you is off for others too) and the people you blocked. */
function ChatCard({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const receipts = useMutation({
    mutationFn: async (chat_receipts: boolean) => unwrap(await api.PATCH("/me", { body: { chat_receipts } })),
    onSuccess: (updated) => queryClient.setQueryData(["me"], updated),
  });
  const { blocks } = useBlocks();
  const setBlocked = useSetBlocked();
  const on = user.chat_receipts !== false;

  return (
    <section className="card stack">
      <h2>Chat</h2>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={on}
          disabled={receipts.isPending}
          onChange={(event) => receipts.mutate(event.target.checked)}
        />
        Read receipts &amp; typing
      </label>
      <p className="muted">
        {on
          ? "People who also have this on see when you've read their messages and when you're typing, and you see theirs."
          : "Nobody sees when you've read their messages or are typing, and you don't see theirs."}
      </p>
      {receipts.error && (
        <p className="error" role="alert">
          {errorMessage(receipts.error)}
        </p>
      )}
      <h3>Blocked people</h3>
      {blocks.isPending && <p className="muted">Loading…</p>}
      {blocks.error && <p className="error">{errorMessage(blocks.error)}</p>}
      {blocks.data?.length === 0 && (
        <p className="muted">
          Nobody. Blocking someone (from a message or your direct chat) hides their messages behind “Show” and stops
          their notifications; it doesn't change circles or location sharing.
        </p>
      )}
      {blocks.data && blocks.data.length > 0 && (
        <ul className="settings-list">
          {blocks.data.map((person) => (
            <li key={person.id} className="settings-item">
              <span className="settings-item-text">
                <span className="settings-item-name">{person.name}</span>
              </span>
              <button
                type="button"
                className="secondary"
                disabled={setBlocked.isPending}
                onClick={() => setBlocked.mutate({ userId: person.id, blocked: false })}
                aria-label={`Unblock ${person.name}`}
              >
                Unblock
              </button>
            </li>
          ))}
        </ul>
      )}
      {setBlocked.error && (
        <p className="error" role="alert">
          {errorMessage(setBlocked.error)}
        </p>
      )}
    </section>
  );
}

function UnitsCard() {
  const choice = useUnitsChoice();
  const regional = resolveUnits("auto") === "metric" ? "kilometres" : "miles";
  return (
    <section className="card stack">
      <h2>Units</h2>
      <label>
        Distances and speeds
        <select value={choice} onChange={(event) => storeUnitsChoice(event.target.value as UnitsChoice)}>
          {UNITS_CHOICES.map((option) => (
            <option key={option} value={option}>
              {option === "auto"
                ? `${UNITS_LABELS.auto} (${regional}, from your browser's region)`
                : UNITS_LABELS[option]}
            </option>
          ))}
        </select>
      </label>
      <p className="muted">Applies in this browser only.</p>
    </section>
  );
}

function MapCard() {
  const shown = useMovingTrailsShown();
  const grouped = useTravelTogether();
  return (
    <section className="card stack">
      <h2>Map</h2>
      <label className="checkbox">
        <input type="checkbox" checked={shown} onChange={(event) => storeMovingTrailsShown(event.target.checked)} />
        Show the last 15 minutes of members on the move
      </label>
      <label className="checkbox">
        <input type="checkbox" checked={grouped} onChange={(event) => storeTravelTogether(event.target.checked)} />
        Group people travelling together
      </label>
      <p className="muted">
        Selecting a member always shows their trip. People in the same car, train or bus show as one marker. Applies in
        this browser only.
      </p>
    </section>
  );
}

function DevicesCard({ nowMs }: { nowMs: number }) {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const devices = useQuery({
    queryKey: DEVICES_KEY,
    queryFn: async () => unwrap(await api.GET("/me/devices")),
  });
  const revoke = useMutation({
    mutationFn: (device: Device) =>
      deleteIdempotent(api.DELETE("/me/devices/{deviceId}", { params: { path: { deviceId: device.id } } })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: DEVICES_KEY }),
  });

  function remove(device: Device) {
    if (device.current) {
      if (window.confirm("Sign out of this browser?")) void signOut();
      return;
    }
    const question = `Sign out ${device.name}? It will stop sharing your location until you sign in on it again.`;
    if (window.confirm(question)) revoke.mutate(device);
  }

  return (
    <section className="card stack">
      <h2>Signed-in devices</h2>
      <p className="muted">Sign out any phone or browser you no longer use or don't recognise.</p>
      {devices.isPending && <p className="muted">Loading devices…</p>}
      {devices.error && <p className="error">{errorMessage(devices.error)}</p>}
      {revoke.error && (
        <p className="error" role="alert">
          {errorMessage(revoke.error)}
        </p>
      )}
      {devices.data && (
        <ul className="settings-list">
          {sortDevices(devices.data).map((device) => (
            <li key={device.id} className="settings-item">
              <span className="settings-item-text">
                <span className="settings-item-name">
                  {device.name}
                  {device.current && " (this browser)"}
                </span>
                <span className="muted settings-item-meta">
                  {platformLabel(device.platform)}
                  {device.app_version && ` ${device.app_version}`} ·{" "}
                  {device.current ? "Active now" : lastActiveText(device.last_seen_at, nowMs)} · Signed in{" "}
                  {new Date(device.created_at).toLocaleDateString()}
                </span>
              </span>
              <button
                type="button"
                className="secondary danger"
                disabled={revoke.isPending && revoke.variables.id === device.id}
                onClick={() => remove(device)}
                aria-label={device.current ? "Sign out of this browser" : `Sign out ${device.name}`}
              >
                Sign out
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ApiTokensCard({ nowMs }: { nowMs: number }) {
  const queryClient = useQueryClient();
  const tokens = useQuery({
    queryKey: TOKENS_KEY,
    queryFn: async () => unwrap(await api.GET("/me/api-tokens")),
  });
  const circles = useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
  });
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  // Back from the show-once secret: return keyboard focus to the form.
  const [focusForm, setFocusForm] = useState(false);
  const revoke = useMutation({
    mutationFn: (token: ApiToken) =>
      deleteIdempotent(api.DELETE("/me/api-tokens/{tokenId}", { params: { path: { tokenId: token.id } } })),
    onSuccess: (_, token) => setCreated((shown) => (shown?.id === token.id ? null : shown)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: TOKENS_KEY }),
  });

  function remove(token: ApiToken) {
    const question = `Revoke "${token.name}"? Anything using it, such as Home Assistant, stops receiving locations.`;
    if (window.confirm(question)) revoke.mutate(token);
  }

  const circleList = circles.data ?? [];
  // Event circles can't have API tokens (409 event_circle).
  const tokenable = tokenCircles(circleList);
  return (
    <section className="card stack">
      <h2>API tokens</h2>
      <p className="muted">
        Access to one circle's members and locations, for Home Assistant and other integrations. Integrations other than
        Home Assistant use server URL <code>{window.location.origin}</code>. Home Assistant's "request update" and
        "import zones" actions need a token that may also request locations and add places.
      </p>
      {created ? (
        <CreatedToken
          token={created}
          onDone={() => {
            setCreated(null);
            setFocusForm(true);
          }}
        />
      ) : circles.data && tokenable.length === 0 ? (
        <p className="muted">
          <Link to="/circles">Create or join a family or friends circle</Link> before adding a token. Events can't have
          tokens.
        </p>
      ) : (
        <CreateTokenForm circles={tokenable} autoFocus={focusForm} onCreated={setCreated} />
      )}
      {(tokens.error ?? circles.error) && <p className="error">{errorMessage(tokens.error ?? circles.error)}</p>}
      {revoke.error && (
        <p className="error" role="alert">
          {errorMessage(revoke.error)}
        </p>
      )}
      {tokens.data && tokens.data.length > 0 && (
        <ul className="settings-list">
          {sortTokens(tokens.data).map((token) => (
            <li key={token.id} className="settings-item">
              <span className="settings-item-text">
                <span className="settings-item-name">{token.name}</span>
                <span className="muted settings-item-meta">
                  {circles.data ? tokenCircleName(token, circleList) : "…"}
                  {` · ${tokenScopeText(token.scope)}`}
                  {token.prefix && (
                    <>
                      {" · "}
                      <code>{token.prefix}…</code>
                    </>
                  )}
                  {` · Created ${new Date(token.created_at).toLocaleDateString()}`} ·{" "}
                  {lastUsedText(token.last_used_at, nowMs)}
                </span>
              </span>
              <button
                type="button"
                className="secondary danger"
                disabled={revoke.isPending && revoke.variables.id === token.id}
                onClick={() => remove(token)}
                aria-label={`Revoke ${token.name}`}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CreateTokenForm(props: {
  circles: readonly Schemas["Circle"][];
  autoFocus: boolean;
  onCreated: (token: ApiTokenCreated) => void;
}) {
  const queryClient = useQueryClient();
  const nameInput = useRef<HTMLInputElement>(null);
  const { autoFocus } = props;
  useEffect(() => {
    if (autoFocus) nameInput.current?.focus();
  }, [autoFocus]);
  const [name, setName] = useState(DEFAULT_TOKEN_NAME);
  const [chosen, setChosen] = useState("");
  const [scope, setScope] = useState<Schemas["ApiTokenScope"]>("circle:read");
  // Default to the first circle, and fall back to it if the chosen one disappears.
  const circleId = props.circles.some((c) => c.id === chosen) ? chosen : (props.circles[0]?.id ?? "");
  const { onCreated } = props;
  const create = useMutation({
    // The secret goes straight to the caller's state, never into the mutation cache.
    mutationFn: async (body: Schemas["ApiTokenCreate"]) => {
      onCreated(unwrap(await api.POST("/me/api-tokens", { body })));
    },
    gcTime: 0,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TOKENS_KEY }),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 403)
        void queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
  const validName = validTokenName(name);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!validName || !circleId) return;
    create.mutate({ name: validName, circle_id: circleId, scope });
  }

  return (
    <form className="token-form" onSubmit={submit}>
      <label>
        Name
        <input
          ref={nameInput}
          value={name}
          maxLength={TOKEN_NAME_MAX}
          required
          onChange={(event) => setName(event.target.value)}
          autoComplete="off"
        />
      </label>
      <label>
        Circle
        <select value={circleId} required onChange={(event) => setChosen(event.target.value)}>
          {props.circles.map((circle) => (
            <option key={circle.id} value={circle.id}>
              {circle.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Access
        <select value={scope} onChange={(event) => setScope(event.target.value as Schemas["ApiTokenScope"])}>
          {(["circle:read", "circle:control"] as const).map((value) => (
            <option key={value} value={value}>
              {tokenScopeText(value)}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" disabled={create.isPending || !validName || !circleId}>
        Create token
      </button>
      {create.error && (
        <p className="error token-form-error" role="alert">
          {errorMessage(create.error, {
            403: "You're no longer a member of that circle.",
            409: "You have too many API tokens. Revoke one you no longer use first.",
          })}
        </p>
      )}
    </form>
  );
}

function CreatedToken({ token, onDone }: { token: ApiTokenCreated; onDone: () => void }) {
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);
  // The form that had focus is gone; keep keyboard users in the flow.
  const copyButton = useRef<HTMLButtonElement>(null);
  useEffect(() => copyButton.current?.focus(), []);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(token.secret);
      setCopied("ok");
    } catch {
      setCopied("failed");
    }
  }

  return (
    <div className="token-created stack">
      <p className="success" role="status">
        Token "{token.name}" created. Copy it now: it won't be shown again.
      </p>
      <code className="token-secret">{token.secret}</code>
      <div className="button-row">
        <button type="button" ref={copyButton} onClick={() => void copy()}>
          {copied === "ok" ? "Copied" : copied === "failed" ? "Copy failed" : "Copy token"}
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function AddPasskeyCard() {
  const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function add() {
    setPending(true);
    setStatus(null);
    try {
      const passkey = await registerPasskey();
      setStatus({ kind: "ok", text: `Passkey "${passkey.name}" added. Use it to sign in next time.` });
    } catch (err) {
      if (!isPasskeyCancelled(err)) setStatus({ kind: "error", text: errorMessage(err) });
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="card stack">
      <h2>Passkeys</h2>
      <p className="muted">Sign in with your fingerprint, face or screen lock instead of a password.</p>
      {status && (
        <p className={status.kind === "ok" ? "success" : "error"} role="status">
          {status.text}
        </p>
      )}
      <button type="button" disabled={pending} onClick={() => void add()}>
        Add a passkey
      </button>
    </section>
  );
}

/** Emergency contacts' error text, else the generic message. */
function contactError(error: unknown) {
  return (error instanceof ApiError && contactErrorText(error.code)) || errorMessage(error);
}

/** Shown when the server can send email, or when contacts remain from when it could. */
function EmergencyContactsCard({ nowMs }: { nowMs: number }) {
  const queryClient = useQueryClient();
  const capabilities = useQuery({
    queryKey: ["capabilities"],
    queryFn: async () => unwrap(await api.GET("/capabilities")),
    staleTime: Infinity,
  });
  const contacts = useQuery({
    queryKey: CONTACTS_KEY,
    queryFn: async () => unwrap(await api.GET("/me/emergency-contacts")),
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: async (body: Schemas["EmergencyContactCreate"]) =>
      unwrap(await api.POST("/me/emergency-contacts", { body })),
    onSuccess: (contact) => {
      setName("");
      setEmail("");
      setSentTo(contact.email);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
  const resend = useMutation({
    mutationFn: async (contact: EmergencyContact) =>
      unwrap(
        await api.POST("/me/emergency-contacts/{contactId}/resend", { params: { path: { contactId: contact.id } } }),
      ),
    onSuccess: (contact) => setSentTo(contact.email),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
  const remove = useMutation({
    mutationFn: (contact: EmergencyContact) =>
      deleteIdempotent(
        api.DELETE("/me/emergency-contacts/{contactId}", { params: { path: { contactId: contact.id } } }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
  const enabled = capabilities.data?.emergency_contacts ?? false;
  const list = contacts.data ?? [];
  if (!enabled && list.length === 0) return null;
  const full = list.length >= MAX_CONTACTS;
  const valid = validContact(name, email);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setSentTo(null);
    add.mutate({ name: name.trim(), email: email.trim() });
  }

  function confirmRemove(contact: EmergencyContact) {
    if (window.confirm(`Remove ${contact.name}? They won't be emailed if you send an SOS.`)) remove.mutate(contact);
  }

  const failure = add.error ?? resend.error ?? remove.error ?? contacts.error;
  return (
    <section className="card stack">
      <h2>Emergency contacts</h2>
      <p className="muted">
        People outside your circles who get an email when you send an SOS, with a link to a page showing your location
        until you say you are safe. Each gets an email first and must accept. Up to {MAX_CONTACTS}.
        {!enabled && " This server can no longer send email, so they won't be contacted."}
      </p>
      {sentTo && (
        <p className="success" role="status">
          We emailed {sentTo} a link to accept. It works for 7 days.
        </p>
      )}
      {failure && (
        <p className="error" role="alert">
          {contactError(failure)}
        </p>
      )}
      {list.length > 0 && (
        <ul className="settings-list">
          {list.map((contact) => {
            const status = contactStatus(contact, nowMs);
            return (
              <li key={contact.id} className="settings-item">
                <span className="settings-item-text">
                  <span className="settings-item-name">{contact.name}</span>
                  <span className={`settings-item-meta ${status === "confirmed" ? "muted" : "error"}`}>
                    {contact.email} · {contactStatusText(contact, nowMs)}
                  </span>
                </span>
                <span className="button-row">
                  {enabled && status !== "confirmed" && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={resend.isPending && resend.variables.id === contact.id}
                      onClick={() => {
                        setSentTo(null);
                        resend.mutate(contact);
                      }}
                      aria-label={`Send ${contact.name} a new link`}
                    >
                      Resend
                    </button>
                  )}
                  <button
                    type="button"
                    className="secondary danger"
                    disabled={remove.isPending && remove.variables.id === contact.id}
                    onClick={() => confirmRemove(contact)}
                    aria-label={`Remove ${contact.name}`}
                  >
                    Remove
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {enabled && !full && (
        <form className="token-form" onSubmit={submit}>
          <label>
            Name
            <input
              value={name}
              maxLength={CONTACT_NAME_MAX}
              required
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              maxLength={254}
              required
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="off"
            />
          </label>
          <button type="submit" disabled={add.isPending || !valid}>
            Add contact
          </button>
        </form>
      )}
    </section>
  );
}

function YourDataCard({ user }: { user: User }) {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const nowMs = useNow(TICK_MS);
  const circles = useCircles();
  const mode = actionMode("delete_account", circles.data ?? []);
  const requests = useMyRequests((circles.data ?? []).some((c) => c.supervision));
  const deletion = recentRequests(requests.data ?? [], ["delete_account"], nowMs)[0];
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const download = useMutation({
    mutationFn: async () => {
      const result = await api.GET("/me/export", { parseAs: "blob" });
      const url = URL.createObjectURL(unwrap(result));
      const link = document.createElement("a");
      link.href = url;
      link.download = exportFileName(result.response.headers.get("Content-Disposition"), new Date());
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/me/delete", { body: { email: typed.trim() } });
      if (!response.ok) unwrap({ error, response, data: undefined });
    },
    // The session no longer exists; this forgets it locally and returns to sign-in.
    onSuccess: () => signOut(),
    // Under "ask" the server made (or found) the request instead: show where it stands.
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "supervised") {
        setConfirming(false);
        setTyped("");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: MY_REQUESTS_KEY }),
          queryClient.invalidateQueries({ queryKey: ["circles"] }),
        ]);
      }
    },
  });
  const asked = remove.error instanceof ApiError && remove.error.code === "supervised";

  function submit(event: FormEvent) {
    event.preventDefault();
    if (deletionConfirmed(typed, user.email)) remove.mutate();
  }

  return (
    <section className="card stack">
      <h2>Your data</h2>
      <p className="muted">
        Location history is kept for a limited time (30 days unless this server is set differently) and then deleted
        automatically. Download a copy of everything stored about you, including your profile, devices, circles and
        retained locations. <Link to="/privacy">Privacy policy</Link>
      </p>
      {download.error && (
        <p className="error" role="alert">
          {errorMessage(download.error, {
            429: "You've downloaded your data several times recently. Try again later.",
          })}
        </p>
      )}
      {deletion && <RequestState request={deletion} label="Delete your account" />}
      {asked && !deletion && <p className="muted">Your guardians have been asked.</p>}
      <div className="button-row">
        <button type="button" className="secondary" disabled={download.isPending} onClick={() => download.mutate()}>
          {download.isPending ? "Preparing download…" : "Download my data"}
        </button>
        {!confirming && !(deletion && deletion.status === "pending") && (
          <button type="button" className="danger" onClick={() => setConfirming(true)}>
            {mode === "ask" ? "Ask to delete account…" : "Delete account…"}
          </button>
        )}
      </div>
      {confirming && (
        <form className="stack" onSubmit={submit}>
          <p>
            Deleting your account permanently removes your location history, places you arrived at or left, devices,
            passkeys and API tokens, and takes you out of every circle. Circles where you are the only member are
            deleted; where you are the only admin, the longest-standing member becomes admin. This can't be undone.
          </p>
          {mode === "ask" && (
            <p>
              Your supervision asks a guardian first: confirming sends them a request, and your account is deleted once
              a guardian approves in each circle that asks for it.
            </p>
          )}
          {mode === "notify" && <p>Your guardians are told.</p>}
          <label>
            Type your email address ({user.email}) to confirm
            <input
              type="email"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={remove.isPending}
            />
          </label>
          {remove.error && !asked && (
            <p className="error" role="alert">
              {mode === "ask" ? askError(remove.error) : errorMessage(remove.error)}
            </p>
          )}
          <div className="button-row">
            <button
              type="submit"
              className="danger"
              disabled={!deletionConfirmed(typed, user.email) || remove.isPending}
            >
              {remove.isPending
                ? "Deleting…"
                : mode === "ask"
                  ? "Ask to delete my account"
                  : "Delete my account permanently"}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={remove.isPending}
              onClick={() => {
                setConfirming(false);
                setTyped("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
